import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { VIDEO_STILLS } from "@/lib/images";
import Reveal from "./Reveal";

const BAND_PHOTOS = [
  { src: "/photos/chant.webp", alt: "La chanteuse de Forty One en concert", span: "sm:col-span-2 sm:row-span-2" },
  { src: "/photos/duo-rouge.webp", alt: "Forty One — duo sur scène sous les lumières rouges", span: "sm:col-span-2" },
  { src: "/photos/chant-nb.webp", alt: "La chanteuse de Forty One — portrait noir et blanc", span: "sm:row-span-2" },
  { src: "/photos/guitare.webp", alt: "Le guitariste de Forty One en concert", span: "" },
  { src: "/photos/basse.webp", alt: "La bassiste de Forty One en concert", span: "" },
  { src: "/photos/batterie-nb.webp", alt: "Le batteur de Forty One — noir et blanc", span: "" },
  { src: "/photos/claviers.webp", alt: "Le claviériste de Forty One sur scène", span: "" },
  { src: "/photos/batterie.webp", alt: "La batterie de Forty One sous les projecteurs", span: "" },
  { src: "/photos/groupe.webp", alt: "Forty One — le groupe au complet sur scène", span: "sm:col-span-2" },
  { src: VIDEO_STILLS[0].src, alt: VIDEO_STILLS[0].alt, span: "" },
];

const Gallery = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="photos" data-testid="photo-gallery-section" className="relative py-24 sm:py-32 bg-[#0F0F16]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-jbmono font-semibold text-amber-400 mb-4">
            Sur scène
          </p>
          <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight uppercase mb-12">
            L'énergie <span className="text-red-500">live</span> en images
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-4 gap-4 auto-rows-[170px] sm:grid-flow-dense">
          {BAND_PHOTOS.map((p, i) => (
            <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} className={p.span}>
              <button
                data-testid={`gallery-photo-${i}`}
                onClick={() => setLightbox(p)}
                className="group relative w-full h-full rounded-2xl overflow-hidden border border-zinc-800 hover:border-amber-500/40 transition-colors duration-500"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-[#09090D]/20 group-hover:bg-[#09090D]/0 transition-colors duration-500" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="lightbox-close-button"
              className="absolute top-6 right-6 text-zinc-400 hover:text-amber-400 transition-colors"
              aria-label="Fermer"
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-w-full max-h-[85vh] rounded-2xl border border-amber-500/20 object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
